import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * <Sprite3D>: a 2D texture drawn as a quad in 3D space, sized by `pixel_size` × the active region
 * (full image, sheet tile or `region_rect`). `useTexture2D` resolves the texture, whatever its
 * source. `meshStandardMaterial` when `shaded`, else
 * `meshBasicMaterial` (`material.cpp:3045`: SHADING_MODE_PER_PIXEL versus SHADING_MODE_UNSHADED).
 */
import { useMemo, useRef } from 'react';
import { visualLayersUserData } from '../../../r3f/visualLayers';
import { useGodotLinearColor } from '../../../r3f/godotColor';
import { node3dGroupSpread, transformFromNode3DProperties } from '../../../r3f/nodeTransform';
import { frameSizePx, frameUvWindow, spriteWrapMode } from '../../../r3f/spriteFrame';
import { WRAP } from '../../../r3f/canvasItemTextureSampler';
import { useWindowedTextureView } from '../../../r3f/useTextureView';
import { flippedUvWindow } from '../../../r3f/uvWindow';
import { useSceneResources } from '../../../r3f/SceneResourcesContext';
import { useAnisotropicFilteringLevel } from '../../../r3f/contexts/AnisotropicFilteringContext';
import { viewportProgramInputs } from '../../../r3f/materialProgramInputs';
import { materialFor2d } from '../../../r3f/materialFor2d';
import { passBlendMaterialProps } from '../../../r3f/materials/surfaceBlend';
import { FadedMaterials } from '../../../r3f/materials/FadedMaterials';
import { useSpriteBase3DColorAccum } from '../../../r3f/spriteBase3DColorAccum';
import { useTexture2D } from '../../../resources/useTexture2D';
import { sourceColorSpace } from '../../../resources/textures/sourceColorSpace';
import { MissingResourcePlaceholder } from '../../../r3f/components/MissingResourcePlaceholder';
import { useBillboard } from '../../../r3f/hooks/useBillboard';
import { useFixedSize } from '../../../r3f/hooks/useFixedSize';
import { FramePoint } from '../../../r3f/components/NodeAabb';
import { useGeometryInstance } from '../../../r3f/visibilityRange/geometryInstance';
import { authoredPlacement, UNPLACED } from '../../../r3f/visibilityRange/placements';
import { spriteQuadAabb, spriteQuadGeometry, spriteQuadRect } from './quad';
import { clamp } from '../../../godot/math/math';
import { useDisposeOnRelease } from '../../../r3f/useDisposeOnRelease';
/** The sprite material's own PBR uniforms (`sprite_3d.cpp:721-722`). */
const SHADED_SCALARS = { metalness: 0, roughness: 1 };
export function Sprite3D({ node, children }) {
    const spriteRef = useRef(null);
    const properties = node.properties;
    const { externalResources, internalResources } = useSceneResources();
    const transform = useMemo(() => transformFromNode3DProperties(properties), [properties]);
    // `sprite_3d.cpp:299` hands the flag to the same cached shader the billboard
    // mode does. Both are per-frame effects on the sprite quad itself. Fixed size first:
    // the billboard freezes the world matrix, so a scale set after it lands a frame late.
    useFixedSize(spriteRef, properties.fixed_size, transform.scale);
    // Godot's billboard is a material-side effect on the sprite quad. The shared
    // hook applies the same modes Label3D uses.
    useBillboard(spriteRef, properties.billboard);
    // The sprite's material samples `texture_albedo` as `source_color` (`material.cpp:969`), so a
    // ViewportTexture gives its decoding view.
    const { texture: sourceTexture, size: reportedSize, missing: textureMissing, } = useTexture2D(properties.texture, externalResources, internalResources, 'decoding');
    // The wrap mode is derived, not fixed: `sprite_3d.cpp:163` reads it off the frame's own UV
    // corners, so only an overrunning window tiles.
    const wrap = spriteWrapMode(sourceTexture ?? undefined, properties);
    const anisotropicFilteringLevel = useAnisotropicFilteringLevel();
    const { blends, joinsShadowPass, side, depthTest, textureFilter } = materialFor2d({
        alphaCut: properties.alpha_cut,
        alphaScissorThreshold: properties.alpha_scissor_threshold,
        // `sprite_3d.cpp:286`: FLAG_TRANSPARENT off disables the whole switch.
        transparentFlag: properties.transparent,
        alphaHashScale: properties.alpha_hash_scale,
        alphaAntialiasingMode: properties.alpha_antialiasing_mode,
        alphaAntialiasingEdge: properties.alpha_antialiasing_edge,
        // DoubleSide by default: Godot's runtime shows a sprite quad from behind too.
        doubleSided: properties.double_sided,
        noDepthTest: properties.no_depth_test,
        textureFilter: properties.texture_filter,
        anisotropicFilteringLevel,
    });
    // `texture_albedo` is a `source_color` sampler (`sprite_3d.cpp:306`), so the view reads as an
    // albedo, and the node's own `texture_filter` lands on it (`material.cpp:3055`). The view is this
    // component's, never the loader's cached entry, since a frame writes its window on it.
    // Flip mirrors through the UV window, where 2D mirrors through mesh scale.
    const frameWindow = useMemo(() => flippedUvWindow(frameUvWindow(sourceTexture ?? undefined, properties), properties.flip_h === true, properties.flip_v === true), [sourceTexture, properties]);
    const displayedTexture = useWindowedTextureView(sourceTexture, sourceTexture && {
        colorSpace: sourceColorSpace(sourceTexture),
        filter: textureFilter,
        wrap: WRAP[wrap],
    }, frameWindow);
    // Quad sizing: pixel_size × the frame's pixel dimensions (1×1 fallback
    // before the image loads keeps the placeholder at expected scale).
    const rect = useMemo(() => {
        const px = frameSizePx(reportedSize, properties);
        const size = { width: px.width * properties.pixel_size, height: px.height * properties.pixel_size };
        return spriteQuadRect(size, properties);
    }, [reportedSize, properties]);
    // The frame decides the box, so a texture still loading leaves the sprite unplaced.
    const placement = useMemo(() => sourceTexture
        ? authoredPlacement(spriteRef, properties.transform, spriteQuadAabb(rect, properties.axis, properties.billboard))
        : UNPLACED, [sourceTexture, rect, properties.transform, properties.axis, properties.billboard]);
    const shadow = useGeometryInstance(placement);
    // `_get_color_accum()` (`sprite_3d.cpp:36-52`) folds the parent sprite's accumulation into this
    // node's modulate, r/g/b and a. Godot multiplies the stored colours and converts once, so the
    // sRGB→linear step stays here, after the product (matching Sprite2D and WorldEnvironment).
    const accum = useSpriteBase3DColorAccum(properties.modulate);
    const color = useGodotLinearColor(accum);
    // The instance fade is per GeometryInstance3D, outside the accumulation: only `modulate`
    // accumulates. It can move the quad to the alpha pass, which no cut arm reaches.
    // TRANSPARENCY_DISABLED never multiplies the modulate alpha into ALPHA (`material.cpp:1836`).
    const opacity = properties.transparent ? clamp(accum.a, 0, 1) : 1;
    const castsShadow = shadow.castShadow && joinsShadowPass;
    // Baked into the geometry, so it stays right under the node's rotation and billboard.
    const geometry = useMemo(() => spriteQuadGeometry(rect, properties.axis), [rect, properties.axis]);
    // `geometry` goes through `<primitive>`, which R3F does not auto-dispose, so this releases the
    // GPU buffers on replacement and unmount.
    useDisposeOnRelease(geometry);
    // Descendants ride in a sibling group with the same transform, not inside the quad: in Godot
    // `billboard` is a shader-side effect that never spins the node's children. Every branch below
    // renders it, placeholder and pending included, so a missing or slow texture keeps the subtree.
    const subtree = children === undefined ? null : _jsx("group", { ...node3dGroupSpread(transform), children: children });
    // Texture failed to load: a magenta-quad placeholder. The DOM `<MissingResourcesPanel>` names the
    // path.
    if (textureMissing) {
        return (_jsxs(_Fragment, { children: [_jsx(MissingResourcePlaceholder, { shape: "plane", name: node.name, position: transform.position, rotation: transform.rotation, scale: transform.scale }), subtree] }));
    }
    // No texture draws nothing (`sprite_3d.cpp:798`), and so does one still pending: `useTexture2D`
    // picks it up on a later render, once the host provides the file.
    if (!displayedTexture) {
        return (_jsxs(_Fragment, { children: [_jsx("group", { ref: spriteRef, name: node.name, ...node3dGroupSpread(transform), children: _jsx(FramePoint, {}) }), subtree] }));
    }
    const material = (pass, attach) => {
        const blend = blends[pass];
        const program = viewportProgramInputs({
            props: {
                attach,
                map: displayedTexture,
                color,
                opacity,
                ...passBlendMaterialProps(blend),
                injection: blend.injection,
                // FLAG_DISABLE_DEPTH_TEST → `render_mode depth_test_disabled` (`material.cpp:863`).
                depthTest,
                side,
            },
            merge: [properties.shaded ? SHADED_SCALARS : undefined],
        });
        return properties.shaded ? (_jsx("meshStandardMaterial", { ...program.props }, program.key)) : (_jsx("meshBasicMaterial", { ...program.props }, program.key));
    };
    return (_jsxs(_Fragment, { children: [_jsxs("mesh", { 
                // The billboard and `fixed_size` hooks pose the quad. The subtree is a sibling, so the
                // cull skipping the quad's draws skips nothing else.
                ref: spriteRef, name: node.name, ...node3dGroupSpread(transform), renderOrder: properties.render_priority, castShadow: castsShadow, receiveShadow: true, onBeforeRender: shadow.onBeforeRender, onAfterRender: shadow.onAfterRender, onBeforeShadow: shadow.onBeforeShadow, onAfterShadow: shadow.onAfterShadow, userData: visualLayersUserData(properties.layers), children: [_jsx("primitive", { object: geometry, attach: "geometry" }), _jsx(FadedMaterials, { children: material })] }), subtree] }));
}
//# sourceMappingURL=Component.js.map